<script setup>
import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { Plus, X, Pencil, Trash2, RotateCcw, Save } from "lucide-vue-next";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2PopulationsStore } from "@stores/survey/resultsV2/resultsV2.populations";
import { useSurveyStore } from "@stores/survey/survey";
import { usePopulationLabels } from "@/composables/usePopulationLabels";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";
import { renderMarkdownInline } from "@utils/renderMarkdown";
import { POPULATION_AUTO_COLORS, populationColor } from "@/lib/chartPalettes";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
import ResultsV2PopulationDrawer from "./ResultsV2PopulationDrawer.vue";

const store = useResultsV2Store();
const populationsStore = useResultsV2PopulationsStore();
const { showAllPopulation, savedPopulations } = storeToRefs(store);
const { canAnalyzeSurvey } = storeToRefs(useSurveyStore());
const { populations } = storeToRefs(populationsStore);
const { filterSummary } = usePopulationLabels();

const showDrawer = ref(false);
const drawerSelection = ref("all");
const popoverOpen = ref(false);

const pillClass = computed(() => (canAnalyzeSurvey.value ? "cursor-pointer hover:bg-muted" : ""));

const activePopulationIds = computed(
  () => new Set(populations.value.map((p) => p.search_group?.id)),
);

function resolveColor(pop) {
  return populationColor(pop.search_group?.token, populations.value);
}

function filterCount(pop) {
  return Object.keys(pop.search_group?.filters || {}).length;
}

function refreshCharts() {
  store.fetchAllChartsData(populations.value);
}

async function handleResetPopulation(pop) {
  await populationsStore.resetPopulation(pop.id);
  refreshCharts();
}

async function handleSavePopulation(pop) {
  await populationsStore.savePopulation(pop.id);
  refreshCharts();
}

function handleEditPopulation(index) {
  drawerSelection.value = index;
  showDrawer.value = true;
}

function handleEditAll() {
  drawerSelection.value = "all";
  showDrawer.value = true;
}

function handleEditPopulations() {
  popoverOpen.value = false;
  drawerSelection.value = populations.value.length ? 0 : "all";
  showDrawer.value = true;
}

async function addSavedToView(saved) {
  await populationsStore.addPopulation({ search_group_id: saved.id });
  return populations.value.length - 1;
}

async function handleAddSavedPopulation(saved) {
  popoverOpen.value = false;
  await addSavedToView(saved);
  refreshCharts();
}

function handleAddBlankPopulation() {
  popoverOpen.value = false;
  populationsStore.createEphemeral({ filters: {} });
  refreshCharts();
}

async function handleEditSavedPopulation(saved) {
  popoverOpen.value = false;
  let index = populations.value.findIndex((p) => p.search_group?.id === saved.id);
  if (index < 0) {
    index = await addSavedToView(saved);
    refreshCharts();
  }
  drawerSelection.value = index;
  showDrawer.value = true;
}

const {
  isOpen: deleteConfirmOpen,
  target: populationToDelete,
  requestDelete,
  confirm: confirmDelete,
} = useConfirmDelete(async (saved) => {
  await store.deleteSavedPopulation(saved.token);
  if (populationsStore.removeBySearchGroupId(saved.id)) {
    refreshCharts();
  }
});

function handleDeleteSavedPopulation(saved) {
  popoverOpen.value = false;
  requestDelete(saved);
}

async function handleRemovePopulation(index) {
  const pop = populations.value[index];
  if (!pop) return;
  await populationsStore.removePopulation(pop.id);
  refreshCharts();
}

function handleDrawerClose() {
  showDrawer.value = false;
}
</script>

<template>
  <div class="flex flex-1 flex-wrap items-center gap-2">
    <!-- "All" pill. Hidden while a quick filter narrows it in place ("All*"): the pill
         swaps immediately, while the chart column waits for the "All*" data
         (allTemporarilyHidden) so the bars morph instead of blanking. -->
    <div
      v-if="showAllPopulation && !populationsStore.allQuickFilterActive"
      class="group flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs font-medium text-primary shadow-xs transition-colors"
      :class="pillClass"
      @click="canAnalyzeSurvey && handleEditAll()">
      <div class="h-2.5 w-2.5 rounded-full" :style="{ backgroundColor: POPULATION_AUTO_COLORS[0] }" />
      All
      <button
        v-if="canAnalyzeSurvey"
        aria-label="Hide All population"
        class="hidden text-muted-foreground hover:text-destructive group-hover:inline-flex"
        @click.stop="store.setShowAllPopulation(false)">
        <X class="h-3 w-3" />
      </button>
    </div>

    <!-- Populations (saved + the quick-filter population, shown as "All*") -->
    <Tooltip
      v-for="(pop, index) in populations"
      :key="pop.id || index"
      side="bottom"
      :delay-duration="150">
      <div
        class="group flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs font-medium text-primary shadow-xs transition-colors"
        :class="[pillClass, populationsStore.isDirty(pop) ? 'border-dashed' : '']"
        @click="canAnalyzeSurvey && handleEditPopulation(index)">
        <div class="h-2.5 w-2.5 shrink-0 rounded-full" :style="{ backgroundColor: resolveColor(pop) }" />
        <span class="max-w-[180px] truncate">{{ pop.search_group?.name }}<span v-if="populationsStore.isNarrowed(pop)">*</span></span>
        <span
          v-if="populationsStore.isNarrowed(pop)"
          class="flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 text-3xs font-semibold tabular-nums text-background">
          {{ filterCount(pop) }}
        </span>
        <button
          v-if="canAnalyzeSurvey"
          aria-label="Remove population"
          class="ml-0.5 hidden text-muted-foreground hover:text-destructive group-hover:inline-flex"
          @click.stop="handleRemovePopulation(index)">
          <X class="h-3 w-3" />
        </button>
      </div>
      <template #content>
        <div class="space-y-2">
          <div
            v-for="(line, lineIdx) in filterSummary(pop.search_group?.filters).split('\n')"
            :key="lineIdx"
            v-html="renderMarkdownInline(line)" />
          <!-- Reset / Save on modified, unsaved populations -->
          <div v-if="populationsStore.isNarrowed(pop)" class="flex items-center gap-1.5 border-t border-primary-foreground/20 pt-2">
            <button
              class="inline-flex items-center gap-1 rounded bg-background px-2 py-0.5 text-xs font-medium text-foreground hover:bg-muted"
              @click="handleResetPopulation(pop)">
              <RotateCcw class="h-3 w-3" />
              Reset
            </button>
            <button
              class="inline-flex items-center gap-1 rounded bg-background px-2 py-0.5 text-xs font-medium text-foreground hover:bg-muted"
              @click="handleSavePopulation(pop)">
              <Save class="h-3 w-3" />
              {{ pop._fromAll ? "Save as new" : "Save" }}
            </button>
          </div>
        </div>
      </template>
    </Tooltip>

    <!-- Add population button -->
    <Popover v-if="canAnalyzeSurvey" v-model:open="popoverOpen">
      <PopoverTrigger as-child>
        <Button variant="outline" size="sm" class="rounded-full shadow-xs">
          <Plus class="mr-1 h-3.5 w-3.5" />
          Add population
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" class="w-56 p-1">
        <!-- Fixed header: title + always-accessible "Manage" -->
        <div class="flex items-center justify-between gap-2 px-2 py-1.5">
          <span class="heading-label">Populations</span>
          <button
            class="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            @click="handleEditPopulations">
            <Pencil class="h-3 w-3" />
            Manage
          </button>
        </div>
        <div class="mb-1 border-t" />

        <!-- Primary action: create a fresh blank population to quick-filter in place -->
        <Button
          variant="ghost"
          size="sm"
          class="w-full justify-start"
          @click="handleAddBlankPopulation">
          <Plus class="shrink-0" />
          New population
        </Button>
        <div class="my-1 border-t" />

        <!-- Scrollable list: "All" + saved populations -->
        <div class="max-h-72 overflow-y-auto">
          <Button
            v-if="!showAllPopulation"
            variant="ghost"
            size="sm"
            class="w-full justify-start font-normal"
            @click="store.setShowAllPopulation(true); popoverOpen = false">
            <div class="h-2 w-2 shrink-0 rounded-full" :style="{ backgroundColor: POPULATION_AUTO_COLORS[0] }" />
            All respondents
          </Button>

          <div
            v-for="saved in savedPopulations"
            :key="saved.token"
            class="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-13"
            :class="activePopulationIds.has(saved.id) ? 'opacity-50' : 'hover:bg-muted'">
            <button
              class="flex flex-1 items-center gap-2 text-left min-w-0"
              :disabled="activePopulationIds.has(saved.id)"
              @click="handleAddSavedPopulation(saved)">
              <div
                class="h-2 w-2 shrink-0 rounded-full"
                :style="{ backgroundColor: saved.color || 'transparent' }" />
              <span class="truncate">{{ saved.name }}</span>
            </button>
            <button
              aria-label="Edit saved population"
              class="shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground"
              @click.stop="handleEditSavedPopulation(saved)">
              <Pencil class="h-3 w-3" />
            </button>
            <button
              aria-label="Delete saved population"
              class="shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive"
              @click.stop="handleDeleteSavedPopulation(saved)">
              <Trash2 class="h-3 w-3" />
            </button>
          </div>

          <p
            v-if="!savedPopulations.length"
            class="px-2 py-1.5 text-xs text-muted-foreground">
            No saved population yet.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  </div>

  <ResultsV2PopulationDrawer
    v-if="canAnalyzeSurvey"
    :open="showDrawer"
    :initial-selection="drawerSelection"
    @close="handleDrawerClose" />

  <ConfirmDialog
    v-model:open="deleteConfirmOpen"
    title="Delete population"
    confirm-label="Delete"
    @confirm="confirmDelete">
    Are you sure you want to delete "{{ populationToDelete?.name }}"? This action cannot be undone.
  </ConfirmDialog>
</template>
